import React from 'react';
import styled from 'styled-components';
import { theme } from '../../styles/theme';
import { FaArrowLeft, FaCalendarAlt, FaMapMarkerAlt, FaGasPump, FaCogs, FaTag } from 'react-icons/fa';
import { CarListing } from '../../firebase/firestore';

interface CarDetailPageProps {
  car: CarListing;
  onBack: () => void;
}

const Container = styled.div`
  padding: ${theme.spacing.lg};
`;

const BackButton = styled.button`
  background: ${theme.colors.white};
  color: ${theme.colors.secondary};
  border: 2px solid ${theme.colors.lightGray};
  padding: ${theme.spacing.md};
  border-radius: ${theme.borderRadius.md};
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  margin-bottom: ${theme.spacing.lg};
`;

const Header = styled.div`
  background: ${theme.colors.white};
  border-radius: ${theme.borderRadius.lg};
  box-shadow: ${theme.shadows.md};
  padding: ${theme.spacing.xl};
  margin-bottom: ${theme.spacing.lg};
`;

const Title = styled.h1`
  font-size: 1.8rem;
  font-weight: 800;
  color: ${theme.colors.secondary};
`;

const Meta = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: ${theme.spacing.md};
  margin-top: ${theme.spacing.md};
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  color: ${theme.colors.mediumGray};
`;

const PriceTag = styled.div`
  background: ${theme.colors.primary};
  color: ${theme.colors.white};
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  border-radius: ${theme.borderRadius.md};
  font-weight: 700;
  margin-top: ${theme.spacing.md};
`;

const Section = styled.section`
  background: ${theme.colors.white};
  border-radius: ${theme.borderRadius.lg};
  box-shadow: ${theme.shadows.md};
  padding: ${theme.spacing.xl};
`;

const CarDetailPage: React.FC<CarDetailPageProps> = ({ car, onBack }) => {
  // Add safety checks for car data
  if (!car || !car.title || car.price === undefined) {
    return (
      <Container>
        <BackButton onClick={onBack}>
          <FaArrowLeft /> Back to listings
        </BackButton>
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <div>Car data is incomplete. Please try again.</div>
        </div>
      </Container>
    );
  }

  return (
    <Container>
      <BackButton onClick={onBack}>
        <FaArrowLeft /> Back to listings
      </BackButton>

      <Header>
        <Title>{car.title}</Title>
        <Meta>
          <MetaItem>
            <FaCalendarAlt /> {car.year || 'N/A'}
          </MetaItem>
          <MetaItem>
            <FaMapMarkerAlt /> {car.location || 'N/A'}
          </MetaItem>
          <MetaItem>
            <FaGasPump /> {car.fuelType || 'N/A'}
          </MetaItem>
          <MetaItem>
            <FaCogs /> {car.transmission || 'N/A'}
          </MetaItem>
        </Meta>
        <PriceTag>
          <FaTag /> {car.currency || 'AED'} {car.price ? car.price.toLocaleString() : 'N/A'}
        </PriceTag>
      </Header>

      <Section>
        <h3 style={{ marginBottom: '0.5rem', color: theme.colors.secondary }}>Overview</h3>
        <p style={{ color: theme.colors.mediumGray, lineHeight: 1.6 }}>
          {car.make} {car.model} in excellent condition. Well-maintained with mileage {car.mileage ? car.mileage.toLocaleString() : 'N/A'} km.
          {car.description}
        </p>
      </Section>
    </Container>
  );
};

export default CarDetailPage;


